import { Box, 
         Card, 
         MenuItem, 
         Select, 
         Typography, 
         useTheme, 
         Tabs, 
         Tab, 
         FormControl,
         InputLabel
       }                                  from "@mui/material"
import { useEffect, 
         useState, 
         useCallback, 
         useMemo 
       }                                  from "react"
import { FilePlus 
       }                                  from "lucide-react"
import { selectDraftTemplates,
         selectPublishedTemplates,
         selectModifiedTemplates,
         selectTemplatesError, 
         selectTemplatesInitDone,
         selectSelectedTemplate,
         selectTemplateAction,
         selectSelectedLocale,
         setSelectedLocaleAction
       }                                  from "store/dataReducers/surveyTemplateSlice"
import { useDispatch, 
         useSelector 
       }                                  from "react-redux"
import { SurveyTemplate }                 from "./SurveyTemplate"
import { APIData
       }                                  from "types/cms/frontendTypes"
import { selectCMSLocales, 
         selectCMSSelectedPilot 
       }                                  from "store/dataReducers/cmsPilotSlice"

export const SurveyTemplatesContainer = () => {

  const theme = useTheme()
  const dispatch = useDispatch()

  const selectedPilot       = useSelector(selectCMSSelectedPilot)!
  const locales             = useSelector(selectCMSLocales)
  const selectedTemplate    = useSelector(selectSelectedTemplate)
  const templatesError      = useSelector(selectTemplatesError)
  const initDone            = useSelector(selectTemplatesInitDone)
  const selectedLocale      = useSelector(selectSelectedLocale)!

  const draftTemplates      = useSelector(selectDraftTemplates)
  const publishedTemplates  = useSelector(selectPublishedTemplates)
  const modifiedTemplates   = useSelector(selectModifiedTemplates)

  const [modifiedTemplate, setModifiedTemplate] = useState<APIData<'api::survey-template.survey-template'> | undefined>(undefined)
  const [publishedTemplate, setPublishedTemplate] = useState<APIData<'api::survey-template.survey-template'> | undefined>(undefined)
  
  const [activeTab, setActiveTab] = useState(0)
  
  const allTemplates = useMemo(() => {
    const publishedTemplatesWithoutModifications = publishedTemplates.filter(
        t => modifiedTemplates.find(m => m.documentId === t.documentId) === undefined)  
    return [...draftTemplates, ...modifiedTemplates, ...publishedTemplatesWithoutModifications]
  }, [draftTemplates, publishedTemplates, modifiedTemplates])

  const handleTemplateDeleted = useCallback(() => {
    // Template deletion is handled by the component itself
  }, [])

  const handleLocaleChange = (locale: string) => {
    dispatch(setSelectedLocaleAction(locale))
  }

  const fetchTemplateData = useCallback(() => {
    if (!selectedTemplate) {
      setModifiedTemplate(undefined)
      setPublishedTemplate(undefined)
      return
    }

    const templateDocumentId = selectedTemplate
    
    const draft = draftTemplates.find(t => t.documentId === templateDocumentId)
    if (draft) {
      setModifiedTemplate(draft)
      setPublishedTemplate(undefined)
    } else {
      const modified = modifiedTemplates.find(t => t.documentId === templateDocumentId)
      const published = publishedTemplates.find(t => t.documentId === templateDocumentId)
      setModifiedTemplate(modified || published)
      setPublishedTemplate(published)
    }
  }, [selectedTemplate, draftTemplates, modifiedTemplates, publishedTemplates])

  useEffect(() => {
    fetchTemplateData()
  }, [fetchTemplateData])

  return (
    
    <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '10px', 
      height: '100%', overflowY: 'auto'}}> 

      {templatesError && (
        <Typography variant="body2" color="error">{templatesError.message}</Typography>
      )}

      {!templatesError && initDone &&
      <Card style={{ padding: '15px', marginBottom: '20px', overflow: 'unset' }}>  
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center' }}>
              <FilePlus size={24} style={{ stroke: theme.palette.primary.main, marginRight: 10 }} />
              Survey Templates
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              Build and structure surveys by using questions from the Question Bank.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>

          <FormControl size="small" sx={{ minWidth: 100, mr: 2 }}>
            <InputLabel sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>Locale</InputLabel>
            <Select
              sx={{ 
                height: '36px', 
                fontSize: '12px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '.MuiSelect-select' : {
                  minHeight: 'unset',
                  color: theme.palette.text.secondary
                }
              }}
              value={selectedLocale}
              onChange={(e) => handleLocaleChange(e.target.value)}
              label="Locale"
            >
              {(selectedPilot.supportedLocales as string[]).map((locale) => (
                <MenuItem key={locale} value={locale} sx={{ color: theme.palette.text.secondary }}>
                  {locales.find(l => l.code === locale)?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

            <Select
              value={selectedTemplate || undefined}
              onChange={(e) => dispatch(selectTemplateAction(e.target.value))}
              sx={{ width: '250px', mr: 1 }}>
              {allTemplates.map((template) => (
                <MenuItem key={template.documentId} value={template.documentId}>
                  {template.label || template.templateType}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {selectedTemplate && (
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                textColor="inherit"
                slotProps={{
                  indicator: {
                    sx: { backgroundColor: theme.palette.text.secondary }
                  }
                }}
                sx={{
                  '& .Mui-selected': {
                    color: theme.palette.text.secondary
                  }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {publishedTemplate ? 'Modified' : 'Draft'}
                    </Box>
                  } 
                />
                <Tab 
                  disabled={!publishedTemplate}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Published
                    </Box>
                  } 
                />
              </Tabs>
            </Box>

            <Box>
              {activeTab === 0 && modifiedTemplate && (
                <SurveyTemplate 
                  editable={true}
                  template={modifiedTemplate}
                  onTemplateDeleted={handleTemplateDeleted}
                  isDraftOrModified={!publishedTemplate}
                  isPublished={false}
                  selectedLocale={selectedLocale}
                />
              )}
              {activeTab === 1 && publishedTemplate && (
                <SurveyTemplate 
                  editable={false}
                  template={publishedTemplate}
                  onTemplateDeleted={handleTemplateDeleted}
                  isDraftOrModified={false}
                  isPublished={true}
                  selectedLocale={selectedLocale}
                />
              )}
            </Box>
          </Box>
        )}
      </Card>
      }

    </div>
  )
}